import { useEffect, useRef, useState } from 'react';
import { messageOf } from '@truecare/api-client';
import {
  IANATimezoneSchema,
  IANA_TIMEZONE_LABELS,
  type AvailabilityCreateRequest,
  type IANATimezone,
} from '@truecare/schemas';
import { Alert, Button, DateInput, Field, Input, useFocusTrap } from '@truecare/ui';

/**
 * Phase 5f-d — availability block create modal.
 *
 * Captures a new availability block — either ONE_OFF (specific date +
 * start/end times) OR WEEKLY (day-of-week + start/end HH:MM + timezone
 * + optional effective range). Discriminated-union shape mirrors the
 * backend AvailabilityCreateRequestSchema in
 * packages/schemas/src/shift/availability.schema.ts.
 *
 * ONE_OFF datetime inputs: the modal collects local datetimes
 * (`datetime-local` input type) and converts to UTC ISO at submit time
 * using `new Date(localStr).toISOString()`. The browser's local TZ is
 * used — for v1 caregivers in Ontario this is America/Toronto. Atikokan
 * caregivers would see times shifted but the submit semantics are
 * correct (the input timestamp gets attached to the date in the
 * caregiver's wall-clock TZ).
 *
 * WEEKLY inputs go through verbatim (HH:MM + IANA timezone + day index
 * are all explicit; no implicit TZ conversion).
 *
 * Modal pattern mirrors <ArtifactUploadModal> + admin's
 * <OverrideActionButton> (role=dialog + aria-modal + Escape close +
 * addEventListener cleanup).
 */

const DAY_OPTIONS: readonly { value: number; label: string }[] = [
  { value: 1, label: 'Monday' },
  { value: 2, label: 'Tuesday' },
  { value: 3, label: 'Wednesday' },
  { value: 4, label: 'Thursday' },
  { value: 5, label: 'Friday' },
  { value: 6, label: 'Saturday' },
  { value: 7, label: 'Sunday' },
];

// Audit #18 — derive the timezone options from the canonical single-source-of-
// truth in @truecare/schemas (all THREE Ontario zones with correct labels)
// instead of a hand-maintained list. The old list omitted America/Winnipeg (the
// actual Central zone) and mislabelled America/Atikokan as "Central" — it is
// EST year-round (Eastern, no DST), not Central. See timezone.schema.ts.
const TIMEZONE_OPTIONS: readonly { value: IANATimezone; label: string }[] =
  IANATimezoneSchema.options.map((tz) => ({ value: tz, label: IANA_TIMEZONE_LABELS[tz] }));

export interface AvailabilityCreateModalProps {
  open: boolean;
  onSubmit: (input: AvailabilityCreateRequest) => Promise<void>;
  onCancel: () => void;
  error?: string | null;
}

export function AvailabilityCreateModal({
  open,
  onSubmit,
  onCancel,
  error,
}: AvailabilityCreateModalProps) {
  const [type, setType] = useState<'ONE_OFF' | 'WEEKLY'>('WEEKLY');
  // ONE_OFF state
  const [startLocal, setStartLocal] = useState<string>('');
  const [endLocal, setEndLocal] = useState<string>('');
  // WEEKLY state
  const [dayOfWeek, setDayOfWeek] = useState<number>(1);
  const [startTime, setStartTime] = useState<string>('09:00');
  const [endTime, setEndTime] = useState<string>('17:00');
  const [timezone, setTimezone] = useState<IANATimezone>(
    'America/Toronto',
  );
  const [effectiveFrom, setEffectiveFrom] = useState<string>('');
  const [effectiveTo, setEffectiveTo] = useState<string>('');

  const [localError, setLocalError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (open) {
      setType('WEEKLY');
      setStartLocal('');
      setEndLocal('');
      setDayOfWeek(1);
      setStartTime('09:00');
      setEndTime('17:00');
      setTimezone('America/Toronto');
      setEffectiveFrom('');
      setEffectiveTo('');
      setLocalError(null);
      setSubmitting(false);
    }
  }, [open]);

  // Focus in on open, trap Tab, Escape-to-close, restore focus to the
  // trigger on close (H-F4). Replaces the prior Escape-only handler.
  useFocusTrap(panelRef, { active: open, onEscape: onCancel });

  if (!open) return null;

  const displayedError = localError ?? error ?? null;

  async function submit(): Promise<void> {
    setLocalError(null);
    let body: AvailabilityCreateRequest;
    if (type === 'ONE_OFF') {
      if (startLocal === '' || endLocal === '') {
        setLocalError('Enter both start and end times.');
        return;
      }
      const startUtc = new Date(startLocal).toISOString();
      const endUtc = new Date(endLocal).toISOString();
      if (new Date(endUtc).getTime() <= new Date(startUtc).getTime()) {
        setLocalError('End time must be after start time.');
        return;
      }
      body = { recurrenceType: 'ONE_OFF', startUtc, endUtc };
    } else {
      body = {
        recurrenceType: 'WEEKLY',
        weeklyDayOfWeek: dayOfWeek,
        weeklyStartLocalTime: startTime,
        weeklyEndLocalTime: endTime,
        weeklyTimezone: timezone,
        ...(effectiveFrom !== '' && { weeklyEffectiveFrom: effectiveFrom }),
        ...(effectiveTo !== '' && { weeklyEffectiveTo: effectiveTo }),
      };
    }
    setSubmitting(true);
    try {
      await onSubmit(body);
    } catch (e) {
      setLocalError(messageOf(e, 'Could not save availability.'));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      ref={panelRef}
      role="dialog"
      aria-modal="true"
      aria-labelledby="availability-create-modal-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-ink/40 p-4"
      data-testid="availability-create-modal"
    >
      <div className="w-full max-w-md rounded-md border border-border bg-cream p-6 max-h-[90vh] overflow-y-auto">
        <h2
          id="availability-create-modal-title"
          className="font-heading text-xl text-forest"
        >
          Add availability
        </h2>

        {displayedError && (
          <Alert
            tone="danger"
            className="mt-3"
            data-testid="availability-create-modal-error"
          >
            {displayedError}
          </Alert>
        )}

        <Field label="Type" className="mt-4">
          <div className="flex gap-2" data-testid="availability-create-modal-type-toggle">
            <Button
              type="button"
              variant={type === 'WEEKLY' ? 'primary' : 'secondary'}
              size="sm"
              aria-pressed={type === 'WEEKLY'}
              onClick={() => setType('WEEKLY')}
              data-testid="availability-create-modal-type-WEEKLY"
            >
              Weekly recurring
            </Button>
            <Button
              type="button"
              variant={type === 'ONE_OFF' ? 'primary' : 'secondary'}
              size="sm"
              aria-pressed={type === 'ONE_OFF'}
              onClick={() => setType('ONE_OFF')}
              data-testid="availability-create-modal-type-ONE_OFF"
            >
              One-time
            </Button>
          </div>
        </Field>

        {type === 'WEEKLY' ? (
          <div className="mt-4 flex flex-col gap-3">
            <Field label="Day of week">
              <select
                className="h-10 rounded-md border border-border bg-white px-2 text-sm text-forest"
                value={dayOfWeek}
                onChange={(e) => setDayOfWeek(Number(e.target.value))}
                data-testid="availability-create-modal-day"
              >
                {DAY_OPTIONS.map((d) => (
                  <option key={d.value} value={d.value}>
                    {d.label}
                  </option>
                ))}
              </select>
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Start time" hint="24-hour, HH:MM">
                <Input
                  type="time"
                  value={startTime}
                  onChange={(e) => setStartTime(e.target.value)}
                  data-testid="availability-create-modal-start-time"
                />
              </Field>
              <Field label="End time" hint="24-hour, HH:MM">
                <Input
                  type="time"
                  value={endTime}
                  onChange={(e) => setEndTime(e.target.value)}
                  data-testid="availability-create-modal-end-time"
                />
              </Field>
            </div>
            <Field label="Timezone">
              <select
                className="h-10 rounded-md border border-border bg-white px-2 text-sm text-forest"
                value={timezone}
                onChange={(e) =>
                  setTimezone(e.target.value as IANATimezone)
                }
                data-testid="availability-create-modal-timezone"
              >
                {TIMEZONE_OPTIONS.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </select>
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field
                label="Effective from (optional)"
                hint="Default: starts today"
              >
                <DateInput
                  value={effectiveFrom}
                  onChange={setEffectiveFrom}
                  data-testid="availability-create-modal-effective-from"
                />
              </Field>
              <Field
                label="Effective until (optional)"
                hint="Default: indefinite"
              >
                <DateInput
                  value={effectiveTo}
                  onChange={setEffectiveTo}
                  data-testid="availability-create-modal-effective-to"
                />
              </Field>
            </div>
          </div>
        ) : (
          <div className="mt-4 flex flex-col gap-3">
            <Field label="Start" hint="Local time">
              <Input
                type="datetime-local"
                value={startLocal}
                onChange={(e) => setStartLocal(e.target.value)}
                data-testid="availability-create-modal-start-local"
              />
            </Field>
            <Field label="End" hint="Local time">
              <Input
                type="datetime-local"
                value={endLocal}
                onChange={(e) => setEndLocal(e.target.value)}
                data-testid="availability-create-modal-end-local"
              />
            </Field>
          </div>
        )}

        <div className="mt-5 flex items-center justify-end gap-3">
          <Button
            type="button"
            variant="secondary"
            onClick={onCancel}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="primary"
            loading={submitting}
            onClick={() => void submit()}
            data-testid="availability-create-modal-submit"
          >
            Add
          </Button>
        </div>
      </div>
    </div>
  );
}
